/**
 * HTTP Service abstraction layer
 * Implements Dependency Inversion Principle by providing an interface for HTTP operations
 */
import axios from 'axios';

/**
 * Abstract HTTP Service interface
 * @abstract
 */
export class HttpService {
  /**
   * Perform GET request
   * @param {string} _url - Request URL
   * @param {Object} _config - Request configuration
   * @returns {Promise<Object>} Response data
   */
  // eslint-disable-next-line no-unused-vars
  async get(_url, _config = {}) {
    throw new Error('Must implement get method');
  }

  /**
   * Perform POST request
   * @param {string} _url - Request URL
   * @param {Object} _data - Request data
   * @param {Object} _config - Request configuration
   * @returns {Promise<Object>} Response data
   */
  // eslint-disable-next-line no-unused-vars
  async post(_url, _data = {}, _config = {}) {
    throw new Error('Must implement post method');
  }

  /**
   * Perform PUT request
   * @param {string} _url - Request URL
   * @param {Object} _data - Request data
   * @param {Object} _config - Request configuration
   * @returns {Promise<Object>} Response data
   */
  // eslint-disable-next-line no-unused-vars
  async put(_url, _data = {}, _config = {}) {
    throw new Error('Must implement put method');
  }

  /**
   * Perform DELETE request
   * @param {string} _url - Request URL
   * @param {Object} _config - Request configuration
   * @returns {Promise<Object>} Response data
   */
  // eslint-disable-next-line no-unused-vars
  async delete(_url, _config = {}) {
    throw new Error('Must implement delete method');
  }
}

/**
 * Axios-based HTTP Service implementation
 */
export class AxiosHttpService extends HttpService {
  constructor() {
    super();
    this.instance = axios.create();
    this.setupInterceptors();
  }

  /**
   * Setup request/response interceptors
   */
  setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        // Add common headers, auth tokens, etc.
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle common error scenarios
        this.handleError(error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Handle HTTP errors consistently
   * @param {Error} error - HTTP error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      console.error('HTTP Error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.config && error.config.url,
        method: error.config && error.config.method
      });
    } else if (error.request) {
      // Request made but no response received
      console.error('Network Error:', error.message);
    } else {
      // Request setup error
      console.error('Request Error:', error.message);
    }
  }

  async get(url, config = {}) {
    const response = await this.instance.get(url, config);
    return response.data;
  }

  async post(url, data = {}, config = {}) {
    const response = await this.instance.post(url, data, config);
    return response.data;
  }

  async put(url, data = {}, config = {}) {
    const response = await this.instance.put(url, data, config);
    return response.data;
  }

  async delete(url, config = {}) {
    const response = await this.instance.delete(url, config);
    return response.data;
  }
}

// Default HTTP service instance
export const httpService = new AxiosHttpService();